// Tier2 Submit 2025 semantic bindings and validation rules not encoded in the field declarations.
// Catalog labels/options remain generated; jurisdiction attachment policy remains in the saved profile.
import type { ResolvedStateField } from '../services/state-fields.ts';
import type { StateFieldValue, TierIIDocumentKind } from '../../tri-shared/types/tierii-state-fields.ts';
import counties from './county-options-2025.json' with { type: 'json' };
// Tier2 Submit 2025 Rev 1, t2s_statedetails.js: DisplayChemicalRequiredFields.
// The commented legacy table in t2s_fields.js is outdated (it incorrectly includes KY).
export const LARGEST_CONTAINER_REQUIRED_STATES = ['AK', 'GA', 'ME', 'NC', 'SC', 'WY'];
export const STATE_POLICIES: Record<
  string,
  {
    equipmentContact?: boolean;
    minimumAttachments?: number;
    minimumEmergencyContacts?: number;
    fireDistrict?: boolean;
    triReportingField?: string;
    sdsExplanation?: boolean;
    county?: { label: string; choices: string[] };
  }
> = {
  CT: {
    equipmentContact: true,
    sdsExplanation: true,
    county: { label: 'Reporting county / planning region', choices: counties.counties.CT },
  },
  ME: { equipmentContact: true },
  NH: { equipmentContact: true },
  RI: { equipmentContact: true, minimumAttachments: 3 },
  VT: { equipmentContact: true },
  CO: { fireDistrict: true, triReportingField: 'bool01' },
  NC: { fireDistrict: true },
  IA: { minimumEmergencyContacts: 2 },
};
export const COMPANY_BINDINGS = ['CO:text02', 'CT:text01', 'NC:text02'];
export const OWNER_BINDINGS = ['NM:text01'];
export const EMAIL_BINDINGS = ['AL:text06', 'ID:text01', 'NH:text01'];
export const RMP_BINDINGS = ['AR:text02', 'OK:text02', 'NC:number02'];
export const ANNUAL_FILING_BINDINGS = ['OH:bool03', 'TX:bool03'];
// Vermont consolidated its LEPCs into a single statewide committee in 2021.
// https://vem.vermont.gov/programs/epcra
export const STATEWIDE_LEPC_BINDINGS = ['VT:text06'];
const SHIPMENT_GROUP_STATES = ['AK', 'CT', 'GA', 'MA', 'ME', 'NH', 'RI', 'VT'];
const REQUIRED_SHIPMENT_STATES = ['AK', 'GA', 'MA', 'ME', 'RI', 'VT'];
const REQUESTED_SHIPMENT_STATES = [...REQUIRED_SHIPMENT_STATES, 'CO', 'NC', 'FL'];
const NRC_RELEASE_STATES = ['AR', 'OK'];
const RCRA_GENERATOR_ANSWERS = ['Yes; Large Quantity Generator (LQG)', 'Yes; Small Quantity Generator (SQG)'];
const WATER_SYSTEM_FIELDS: Record<string, [string, string]> = {
  CT: ['text06', 'text07'],
  ME: ['text06', 'text07'],
  RI: ['text02', 'text03'],
  VT: ['text05', 'text07'],
};
const COLORADO_FACILITY_DESCRIPTIONS: Record<string, string[]> = {
  'Oil and Gas': ['bool02', 'bool03', 'bool04', 'bool05'],
  Agriculture: ['bool07', 'bool08', 'bool09'],
};
export const present = (value: StateFieldValue) => value != null && (typeof value !== 'string' || value.trim() !== '');
export const ATTACHMENT_FIELDS: Record<string, Record<string, TierIIDocumentKind>> = {
  CT: { bool01: 'sds', bool02: 'site-plan' },
  NH: { bool01: 'sds' },
  NC: { bool01: 'floor-plan', bool02: 'rcra-plan' },
  VA: { text01: 'certification' },
  OH: { bool05: 'confidential-location', bool07: 'certification' },
};

export function attachmentFieldApplies(state: string, field: string, fields: ResolvedStateField[]) {
  if (state === 'NC' && field === 'bool02')
    return RCRA_GENERATOR_ANSWERS.includes(String(fields.find((f) => f.field === 'text01')?.value));
  return true;
}

interface RuleContext {
  fields: ResolvedStateField[];
  state: string;
  value: (field: string) => StateFieldValue;
  required: Set<string>;
  issues: string[];
}
type StateRule = (context: RuleContext) => void;

const nrcReleaseRule: StateRule = ({ fields, state, value, issues }) => {
  if ((present(value('text07')) || present(value('text08'))) && !stateFieldApplies(fields, state, 'facility', 'text07'))
    issues.push('NRC dates or case numbers require a Yes spill/release answer.');
};

const FACILITY_RULES: Record<string, StateRule> = {
  ...Object.fromEntries(NRC_RELEASE_STATES.map((state) => [state, nrcReleaseRule])),
  TX: ({ fields, state, required }) => {
    if (stateFieldApplies(fields, state, 'facility', 'date02')) required.add('date02');
    if (stateFieldApplies(fields, state, 'facility', 'date01')) required.add('date01');
  },
  NC: ({ fields, state, required }) => {
    if (stateFieldApplies(fields, state, 'facility', 'date01')) required.add('date01');
    if (stateFieldApplies(fields, state, 'facility', 'text04')) {
      required.add('bool02');
      required.add('text04');
    }
  },
  CO: ({ value, issues }) => {
    const facilityType = String(value('text01'));
    const group = COLORADO_FACILITY_DESCRIPTIONS[facilityType] ?? [];
    if (group.length && !group.some((k) => value(k) === true))
      issues.push('Select at least one description for the facility type.');
    if (facilityType !== 'Oil and Gas' && COLORADO_FACILITY_DESCRIPTIONS['Oil and Gas']!.some((k) => value(k) === true))
      issues.push('Oil and gas descriptions require the Oil and Gas facility type.');
    if (facilityType !== 'Agriculture' && COLORADO_FACILITY_DESCRIPTIONS.Agriculture!.some((k) => value(k) === true))
      issues.push('Agricultural descriptions require the Agriculture facility type.');
  },
  OH: ({ fields, state, value, issues }) => {
    if (['bool01', 'bool02', 'bool03'].filter((k) => value(k) === true).length > 1)
      issues.push('Choose only one type of filing.');
    if (present(value('text03')) && !stateFieldApplies(fields, state, 'facility', 'text03'))
      issues.push('A previous facility name requires Facility name has changed.');
    if (present(value('text08')) && !stateFieldApplies(fields, state, 'facility', 'text08'))
      issues.push('A previous owner requires Owner / Operator has changed.');
  },
};

const shipmentGroupRule: StateRule = ({ fields, state, value, required, issues }) => {
  // Vendor validation IDs 252-254 and 271-273: frequency determines zero versus positive amounts.
  const frequency = value('number02');
  if (frequency === 0)
    ['number01', 'number03', 'number04', 'text02', 'text04'].forEach((field) => required.delete(field));
  for (const field of fields.filter((field) => ['number01', 'number03', 'number04'].includes(field.field))) {
    const amount = value(field.field);
    if (typeof amount !== 'number') continue;
    if (frequency === 0 && amount !== 0) issues.push(field.label + ' must be zero when Frequency of Shipment is zero.');
    if (frequency !== 0 && amount <= 0)
      issues.push(field.label + ' must be greater than zero unless Frequency of Shipment is zero.');
  }
  const modes = fields.filter((f) => f.field.startsWith('bool')).map((f) => f.field);
  modes.forEach((k) => required.delete(k));
  if (
    frequency !== 0 &&
    REQUIRED_SHIPMENT_STATES.includes(state) &&
    shipmentQuestionsApply(fields, state) &&
    !modes.some((k) => value(k) === true)
  )
    issues.push('Select at least one mode of shipment.');
  if (value('bool07') === true) required.add('text01');
  else required.delete('text01');
  if (present(value('text01')) && value('bool07') !== true)
    issues.push('An other shipment description requires the Other shipment mode.');
};

const CHEMICAL_RULES: Record<string, StateRule> = {
  ...Object.fromEntries(SHIPMENT_GROUP_STATES.map((state) => [state, shipmentGroupRule])),
  FL: ({ value, issues }) => {
    if (
      value('text05') !== 'Not Shipped' &&
      !['bool01', 'bool02', 'bool03', 'bool04', 'bool05'].some((k) => value(k) === true)
    )
      issues.push('Select at least one mode of shipment.');
  },
  NC: ({ value, required, issues }) => {
    if (value('text03') !== 'Not Shipped' && !['bool01', 'bool02', 'bool03', 'bool04'].some((k) => value(k) === true))
      issues.push('Select at least one mode of shipment.');
    if (value('bool01') === true) required.add('text01');
    if (value('bool02') === true) required.add('text02');
  },
  CO: ({ value, required, issues }) => {
    if (value('bool01') === true) required.add('text01');
    if (value('bool02') === true) required.add('text02');
    if (!['bool01', 'bool02', 'bool03'].some((k) => value(k) === true))
      issues.push('Select at least one delivery method.');
    if (present(value('text01')) && value('bool01') !== true) issues.push('Rail car details require Rail delivery.');
    if (present(value('text02')) && value('bool02') !== true) issues.push('Trailer details require Truck delivery.');
  },
};

function shipmentQuestionsApply(fields: ResolvedStateField[], state: string) {
  return !['MA', 'VT'].includes(state) || fields.some((field) => field.required);
}

/** Conditional prompts use the same controlling answers as the validation rules. */
export function stateFieldApplies(
  fields: ResolvedStateField[],
  state: string,
  scope: 'facility' | 'chemical',
  field: string,
): boolean {
  const value = (key: string) => fields.find((item) => item.field === key)?.value ?? null;
  if (scope === 'facility') {
    const water = WATER_SYSTEM_FIELDS[state];
    if (water && field === water[1]) return value(water[0]) === 'Yes';
    if (NRC_RELEASE_STATES.includes(state) && ['text07', 'text08'].includes(field)) return value('text06') === 'Yes';
    if (state === 'TX' && field === 'date02') return value('text05') === 'Yes';
    if (state === 'TX' && field === 'date01') return value('text03') === 'Yes';
    if (state === 'NC' && field === 'date01') return value('text08') === 'Yes';
    if (state === 'NC' && ['text04', 'bool02'].includes(field))
      return RCRA_GENERATOR_ANSWERS.includes(String(value('text01')));
    if (state === 'OH' && field === 'text03') return value('bool04') === true;
    if (state === 'OH' && field === 'text08') return value('bool06') === true;
    if (state === 'CO') {
      for (const [type, group] of Object.entries(COLORADO_FACILITY_DESCRIPTIONS)) {
        if (group.includes(field)) return value('text01') === type;
      }
    }
  } else {
    if (SHIPMENT_GROUP_STATES.includes(state) && field === 'text01') return value('bool07') === true;
    if (['CO', 'NC'].includes(state) && field === 'text01') return value('bool01') === true;
    if (['CO', 'NC'].includes(state) && field === 'text02') return value('bool02') === true;
  }
  return true;
}

export function stateFieldContext(fields: ResolvedStateField[], state: string, scope: 'facility' | 'chemical') {
  const values = Object.fromEntries(fields.map((f) => [f.field, f.value]));
  const context: RuleContext = {
    fields,
    state,
    value: (field) => values[field] ?? null,
    required: new Set(fields.filter((f) => f.required).map((f) => f.field)),
    issues: [],
  };
  if (scope === 'facility') {
    const water = WATER_SYSTEM_FIELDS[state];
    if (water && stateFieldApplies(fields, state, scope, water[1])) context.required.add(water[1]);
    FACILITY_RULES[state]?.(context);
  } else CHEMICAL_RULES[state]?.(context);
  // Requested as a group; no individual shipment mode is mandatory.
  const requested = new Set(
    scope === 'chemical' && shipmentQuestionsApply(fields, state) && REQUESTED_SHIPMENT_STATES.includes(state)
      ? fields.filter((field) => field.field.startsWith('bool')).map((field) => field.field)
      : [],
  );
  // A required choice of at least one facility description must be visible before answering.
  if (scope === 'facility' && state === 'CO')
    for (const field of COLORADO_FACILITY_DESCRIPTIONS[String(context.value('text01'))] ?? []) requested.add(field);
  return { required: context.required, requested, issues: context.issues };
}
